import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMonarchSlot } from '@redventures/cohesion-utils-react';
import { setSelectedSweetener } from '~/redux/actions/products';
import { useLTOBanner } from './useLTOBannerUpdated';
import { ReduxState } from '~/types/redux';
import { ngcFlowDefaultValue, NgcFlowSlotData } from '../../monarch/Config | NGC Flow/default';

interface UseClaimRewardsReturn {
  checked: boolean;
  toggle: (checked: boolean) => void;
  rewardType: 'nflst' | 'vrc' | null;
  displayRewards: boolean;
  claimRewardsLocation: 'default' | 'header';
}

/**
 * Hook for managing claim rewards toggle state and determining which reward type to display
 * based on the LTO banner displayStyle (NFLST or VRC).
 * State is read from Redux to ensure all components share the same state.
 */
export const useClaimRewards = (): UseClaimRewardsReturn => {
  const dispatch = useDispatch();
  const { ltoBanner } = useLTOBanner();
  const { products } = useSelector((store: ReduxState) => store);

  // Use the ngcFlow traffic flow slot for NGC configuration values
  const { data: ngcFlowData } = useMonarchSlot<NgcFlowSlotData>('cartConfigNgcFlows', {
    default: ngcFlowDefaultValue,
  });

  const claimRewardsLocation = useMemo(() => {
    // Get claimRewardsLocation from the NGC flow slot
    return ngcFlowData?.claimRewardsLocation || 'default';
  }, [ngcFlowData]);

  // Read state from Redux instead of local useState
  const selectedSweetener = products?.planPageSelections?.selectedSweetener;

  // Determine reward type based on LTO banner displayStyle
  const displayStyle = ltoBanner?.displayStyle;
  const rewardType = displayStyle === 'nflst' ? 'nflst' : 'vrc';
  const displayRewards = Boolean(rewardType);

  // Toggle is checked unless explicitly set to 'skip'
  // This handles the case where Redux is initialized to 'visa' but NFLST is displayed
  const checked = selectedSweetener?.value !== 'skip';

  const toggle = (checked: boolean) => {
    // Dispatch appropriate sweetener based on reward type
    // This updates Redux state which all components will read from
    if (rewardType === 'nflst') {
      const selected = checked ? { value: 'nflst', label: 'Claim Rewards' } : { value: 'skip', label: 'Skip Rewards' };
      dispatch(setSelectedSweetener(selected));
    } else if (rewardType === 'vrc') {
      const selected = checked ? { value: 'visa', label: 'Claim Rewards' } : { value: 'skip', label: 'Skip Rewards' };
      dispatch(setSelectedSweetener(selected));
    }
  };

  return {
    checked,
    toggle,
    rewardType,
    displayRewards,
    claimRewardsLocation,
  };
};
